import {
  CSSProperties,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { styled } from '@mui/material'
import { TextParser } from '@weblite/web-textparser'
import { IMessage } from '@weblite/ts-action/message'
import { SearchContext } from '$comp/_app/components/chat/room'
import { showPhoneMenu } from '$comp/modals/menus/phone-menu'
import { EMOJI_SPRITE_SHEET } from '$helper/constants/emoji'
import { identifierRenderer } from './identifier-renderer'
import { hashtagRenderer } from './hashtag-renderer'
import { hyperLinkRenderer } from './hyper-link-renderer'
import { linkRenderer } from './link-renderer'
import { highlightRenderer } from './highlight'

const CONTAINER_SIDE_PADDING = 10
const parsers = [
  { name: 'emoji', args: { spritesheeturl: EMOJI_SPRITE_SHEET } },
  'code',
  { name: 'customLink', fn: linkRenderer },
  { name: 'hashtag', fn: hashtagRenderer },
  { name: 'identifier', fn: identifierRenderer },
  { name: 'hyperLink', fn: hyperLinkRenderer },
  { name: 'highlight', fn: highlightRenderer },
  { name: 'phone', args: { onClick: showPhoneMenu } },
]

interface RootProps {
  oneLine: boolean
}

const Container = styled('div', {
  shouldForwardProp: prop => !['oneLine'].includes(prop as any),
})<RootProps>(({ theme, oneLine }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  /* @noflip */
  alignItems: 'flex-end',
  boxSizing: 'border-box',
  /* @noflip */
  padding: `${oneLine ? 10 : 8}px ${CONTAINER_SIDE_PADDING}px 10px`,
  color: theme.palette.neutral1000,
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
  textAlign: 'initial',
  ...theme.typography.paragraph3,
  '--emoji-size': '18px',
}))

const StatusContainer = styled('div')`
  flex-grow: 1;
  /* @noflip */
  margin: 8px 0 -2px;
`

export function Text({
  text,
  statusComp,
  rootStyle = undefined,
  links = [],
}: {
  text: string
  statusComp: JSX.Element
  rootStyle?: CSSProperties
  links?: IMessage['links']
}) {
  const statusRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [oneLine, setOneLine] = useState(false)
  const searchQuery = useContext(SearchContext)

  useLayoutEffect(
    () =>
      setOneLine(
        Boolean(
          containerRef.current?.offsetWidth &&
            statusRef.current?.offsetWidth !==
              containerRef.current.offsetWidth - 2 * CONTAINER_SIDE_PADDING,
        ),
      ),
    [text],
  )

  return (
    <Container
      ref={containerRef}
      oneLine={oneLine}
      style={rootStyle}
      onDoubleClick={e => e.stopPropagation()}
      data-needs-selection
    >
      <div dir="auto" style={{ unicodeBidi: 'plaintext' }}>
        <TextParser
          text={text}
          parsers={parsers}
          query={searchQuery}
          links={links.map(({ url }) => url)}
        />
      </div>

      <StatusContainer ref={statusRef}>{statusComp}</StatusContainer>
    </Container>
  )
}
